/**
 * CheckboxPropertyEditor
 *
 * Reusable checkbox component for property editors
 * SRP: Responsible ONLY for rendering a checkbox and handling changes
 * Used by: required, disabled, hidden, readonly, showHeader, autoPlay, etc.
 */

import React, { useCallback, useMemo } from 'react';

export interface CheckboxPropertyEditorProps {
  /** Display label for the checkbox */
  label: string;

  /** Current checked state */
  value: boolean;

  /** Called when checkbox is toggled */
  onChange: (value: boolean) => void;

  /** Field is disabled */
  disabled?: boolean;

  /** Help text shown below checkbox */
  helpText?: string;

  /** Custom CSS class */
  className?: string;
}

/**
 * CheckboxPropertyEditor component
 * Renders a simple checkbox for toggling boolean properties
 */
export const CheckboxPropertyEditor: React.FC<CheckboxPropertyEditorProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  helpText,
  className = '',
}) => {
  // Memoize handler to prevent unnecessary re-renders
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.currentTarget.checked);
    },
    [onChange],
  );

  const inputId = useMemo(
    () => `checkbox-editor-${label.replace(/\s+/g, '-').toLowerCase()}`,
    [label],
  );

  return (
    <div className={`af-property-editor__group af-property-editor__group--checkbox ${className}`}>
      <div className="af-property-editor__checkbox-wrapper">
        <input
          id={inputId}
          type="checkbox"
          checked={value}
          onChange={handleChange}
          disabled={disabled}
          className="af-property-editor__checkbox"
          aria-label={label}
          aria-describedby={helpText ? `${inputId}-help` : undefined}
        />
        <label htmlFor={inputId} className="af-property-editor__checkbox-label">
          {label}
        </label>
      </div>

      {helpText && (
        <div id={`${inputId}-help`} className="af-property-editor__help">
          {helpText}
        </div>
      )}
    </div>
  );
};

CheckboxPropertyEditor.displayName = 'CheckboxPropertyEditor';
